import { ChangeEvent, useCallback, useMemo } from 'react';
import { Option, RenderIf } from 'react-rainbow-components';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import ButtonIcon from 'components/ButtonIcon';
import Trash from 'components/icons/trash';
import Picklist from 'components/Picklist';
import {
    Container, InputElements, StyledPicklist, StyledInput,
} from './styled';
import {
    basicOperators,
    booleanOperators,
    numberOperators,
    textOperators,
} from './operators';
import { Operator } from './types';

interface FiltersConditionProps {
    condition?: any;
    onChange?: (value: any) => void;
    onRequestRemove?: () => void;
}

const Operators = ({ operators = [] }: { operators?: Operator[] }) => operators.map((operator) => (
    <Option
        key={operator.name}
        name={operator.name}
        label={operator.label || operator.name}
        value={operator}
        searchableText={`${operator.name} ${operator.label}`}
    />
));

const FilterCondition = ({
    condition = {},
    onChange = () => {},
    onRequestRemove = () => {},
}: FiltersConditionProps) => {
    const handleChangeAttribute = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => onChange({ ...condition, a: event.target.value }),
        [condition, onChange],
    );

    const handleChangeValue = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => onChange({ ...condition, b: event.target.value }),
        [condition, onChange],
    );

    const handleChangeValueBooleanOperator = useCallback(
        ({ value }: PicklistValue) => onChange({ ...condition, b: value }),
        [condition, onChange],
    );

    const handleChangeOperator = useCallback(
        ({ value }: PicklistValue) => onChange({ ...condition, o: value.name }),
        [condition, onChange],
    );

    const selectedOperator = useMemo(
        () => {
            const operator = [
                ...basicOperators,
                ...booleanOperators,
                ...numberOperators,
                ...textOperators,
            ].find(({ name }) => name === condition.o);
            if (operator) {
                return {
                    name: operator.name,
                    label: operator.label || operator.name,
                    value: operator,
                    searchableText: `${operator.name} ${operator.label}`,
                };
            }

            return undefined;
        },
        [condition.o],
    );

    const inputType = useMemo(() => {
        if (selectedOperator) {
            return selectedOperator.name.split(':').at(0) as string;
        }
        return undefined;
    }, [selectedOperator]);

    const requiredOperands = selectedOperator?.value.requiredOperands ?? 2;

    return (
        <Container>
            <InputElements>
                <StyledInput
                    labelAlignment="left"
                    placeholder="Property path. Ex: data.some.atttribute"
                    value={condition.a}
                    onChange={handleChangeAttribute}
                />
                <StyledPicklist
                    enableSearch
                    size="small"
                    value={selectedOperator}
                    placeholder="Select operator..."
                    onChange={handleChangeOperator}
                >
                    <Option label="Basic operators" variant="header" />
                    <Operators operators={basicOperators} />
                    <Option label="Boolean operators" variant="header" />
                    <Operators operators={booleanOperators} />
                    <Option label="Number operators" variant="header" />
                    <Operators operators={numberOperators} />
                    <Option label="Text operators" variant="header" />
                    <Operators operators={textOperators} />
                </StyledPicklist>
                <RenderIf isTrue={requiredOperands > 1 && inputType !== 'boolean'}>
                    <StyledInput
                        placeholder="Enter value"
                        labelAlignment="left"
                        value={condition.b}
                        onChange={handleChangeValue}
                    />
                </RenderIf>
                <RenderIf isTrue={requiredOperands > 1 && inputType === 'boolean'}>
                    <Picklist
                        value={{
                            name: condition.b,
                            label: condition.b,
                            value: condition.b,
                        }}
                        onChange={handleChangeValueBooleanOperator}
                    >
                        <Option label="true" value="true" name="true" />
                        <Option label="false" value="false" name="false" />
                    </Picklist>
                </RenderIf>
            </InputElements>
            <ButtonIcon
                size="small"
                borderRadius="semi-rounded"
                icon={<Trash />}
                onClick={onRequestRemove}
            />
        </Container>
    );
};

export default FilterCondition;
